import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Formik, Form, Field } from "formik";
import { type } from "arktype";
import { arktypeToFormikValidate } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShareLink } from "@/components/auth/ShareLink";
import { IconPicker } from "@/components/shared/IconPicker";

const clientSchema = type({
  name: "string >= 1",
  icon: "string",
  description: "string",
});

export function ClientCreate() {
  const { jwt } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdJwt, setCreatedJwt] = useState<string | null>(null);

  if (createdJwt) {
    return (
      <div className="space-y-4 md:space-y-6 max-w-md mx-auto">
        <div className="text-center">
          <h1 className="text-heading font-bold mb-1">🎉 Partner Created!</h1>
          <p className="text-body text-app-text-muted">Share this link with your partner.</p>
        </div>
        <ShareLink
          url={`/client/${createdJwt}`}
          title="Partner Access Link"
          description="Your partner will use this link to set up their passcode and access their dashboard."
        />
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(`/admin/${jwt}/clients`)}
        >
          ← Back to Partners
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <Card className="border-t-4 border-t-app-primary pt-0">
        <CardHeader className="bg-gradient-to-r from-app-primary/5 to-transparent rounded-t-lg pt-4">
          <CardTitle className="text-heading">✨ Add New Partner</CardTitle>
        </CardHeader>
        <CardContent>
          <Formik
            initialValues={{ name: "", icon: "🧑", description: "" }}
            validate={arktypeToFormikValidate(clientSchema)}
            onSubmit={async (values) => {
              setLoading(true);
              setError(null);
              try {
                const res = await fetch(`/api/admin/${jwt}/clients`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "X-Passcode": JSON.parse(
                      sessionStorage.getItem("choreapp_passcode") || "{}",
                    ).passcode || "",
                  },
                  body: JSON.stringify(values),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error);
                setCreatedJwt(data.jwt);
              } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to create partner");
              } finally {
                setLoading(false);
              }
            }}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="space-y-1">
                    <Label>Icon</Label>
                    <IconPicker value={values.icon} onSelect={(emoji) => setFieldValue("icon", emoji)} />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="name">Name</Label>
                    <Field as={Input} id="name" name="name" placeholder="Partner's name" autoFocus />
                    {touched.name && errors.name && (
                      <p className="text-info text-app-danger">{errors.name}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Field as={Textarea} id="description" name="description" placeholder="Optional note..." />
                </div>

                {error && <p className="text-info text-app-danger text-center">{error}</p>}

                <Button type="submit" className="w-full bg-app-primary hover:bg-app-primary-hover" disabled={loading}>
                  {loading ? "Creating..." : "Create Partner"}
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
}
